<template>
	<v-card outlined>
		<v-card-title class="pb-2">
			Prodotti di {{ siteName }}
			<v-chip x-small class="ml-2">{{ totale }}</v-chip>
		</v-card-title>
		<v-card-text class="pb-0">
			<v-row dense align="center">
				<v-col cols="12" md="6">
					<v-text-field v-model="cerca" label="Cerca per nome, brand, SKU o EAN" outlined dense hide-details clearable append-icon="mdi-magnify" @keyup.enter="ricarica" @click:append="ricarica" @click:clear="onClear"></v-text-field>
				</v-col>
				<v-col cols="12" md="6">
					<v-btn-toggle v-model="filtro" mandatory dense color="primary" @change="ricarica">
						<v-btn small value="all">Tutti</v-btn>
						<v-btn small value="true">Abbinati</v-btn>
						<v-btn small value="false">Non abbinati</v-btn>
					</v-btn-toggle>
				</v-col>
			</v-row>
		</v-card-text>

		<!-- la tabella nasce dopo il primo caricamento: con il totale a zero riporterebbe a 1 la pagina letta dall'URL -->
		<v-data-table
			v-if="pronto"
			:headers="headers"
			:items="righe"
			:options.sync="opzioni"
			:server-items-length="totale"
			:loading="caricamento"
			item-key="_id"
			dense
			:footer-props="{ 'items-per-page-options': [25, 50, 100] }"
		>
			<template v-slot:[`item.image`]="{ item }">
				<v-img v-if="immagine(item)" :src="immagine(item)" max-width="48" max-height="48" contain class="my-1"></v-img>
				<v-icon v-else color="grey lighten-1">mdi-image-off-outline</v-icon>
			</template>
			<template v-slot:[`item.name`]="{ item }">
				<div>{{ (item.data && item.data.name) || '—' }}</div>
				<a class="caption" :href="item.sourceUrl" target="_blank" rel="noopener noreferrer">pagina sul sito<v-icon x-small color="primary" class="ml-1">mdi-open-in-new</v-icon></a>
				<v-chip v-if="item.gone" x-small color="grey" outlined class="ml-2">Non più trovato</v-chip>
			</template>
			<template v-slot:[`item.brand`]="{ item }">{{ (item.data && item.data.brand) || '—' }}</template>
			<template v-slot:[`item.codici`]="{ item }">
				<div class="caption">EAN {{ (item.data && item.data.ean) || '—' }}</div>
				<div class="caption">SKU {{ (item.data && item.data.sku) || '—' }}</div>
			</template>
			<template v-slot:[`item.price`]="{ item }">
				<span class="text-no-wrap">{{ prezzo(item) }}</span>
			</template>
			<template v-slot:[`item.match`]="{ item }">
				<div class="d-flex align-center">
					<div>
						<v-chip x-small :color="chip(item).colore" :outlined="!item.match" :text-color="item.match ? 'white' : undefined" class="mr-1" :title="titoloChip(item)">
							<v-icon v-if="item.matchLocked" x-small left>mdi-lock</v-icon>{{ chip(item).testo }}
						</v-chip>
						<div v-if="item.item" class="caption">
							{{ item.item.name }}
							<span class="grey--text">{{ (item.match && item.match.variantSku) || item.item.sku || '' }}</span>
						</div>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small title="Correggi abbinamento" @click="$emit('match', item)"><v-icon small>mdi-pencil</v-icon></v-btn>
				</div>
			</template>
		</v-data-table>
	</v-card>
</template>

<script>
import _ from 'lodash';
import { METODI_MATCH, testoErrore } from './testi';
import { money } from '../../views/competitor/pricing';
import { rememberListRoute } from '../core/list/listReturn';

// stato della tabella nell'URL della pagina Competitor (accanto a `site`)
const CHIAVI = ['q', 'abbinati', 'page', 'perPage'];
const PER_PAGINA = [25, 50, 100];

export default {
	name: 'CompetitorSiteProductsTable',
	props: {
		siteId: { type: String, required: true },
		siteName: { type: String, default: '' },
		refreshKey: { type: Number, default: 0 },
	},
	data() {
		const q = this.$route.query;
		const perPage = parseInt(q.perPage, 10);
		return {
			cerca: q.q ? String(q.q) : '',
			filtro: q.abbinati === 'true' || q.abbinati === 'false' ? q.abbinati : 'all',
			righe: [],
			totale: 0,
			caricamento: false,
			pronto: false,
			opzioni: { page: parseInt(q.page, 10) > 0 ? parseInt(q.page, 10) : 1, itemsPerPage: PER_PAGINA.includes(perPage) ? perPage : 50 },
			headers: [
				{ text: '', value: 'image', sortable: false, width: 60 },
				{ text: 'Prodotto', value: 'name', sortable: false },
				{ text: 'Brand', value: 'brand', sortable: false },
				{ text: 'Codici', value: 'codici', sortable: false },
				{ text: 'Prezzo', value: 'price', sortable: false },
				{ text: 'Abbinamento', value: 'match', sortable: false, width: 300 },
			],
		};
	},
	watch: {
		siteId() {
			this.cerca = '';
			this.filtro = 'all';
			this.ricarica();
		},
		refreshKey() {
			this.carica();
		},
		opzioni: {
			deep: true,
			handler() {
				if (this.pronto) this.carica();
			},
		},
	},
	async mounted() {
		await this.carica();
		this.pronto = true;
	},
	methods: {
		immagine(item) {
			return item.data && item.data.images && item.data.images[0];
		},
		prezzo(item) {
			const d = item.data || {};
			return money(d.price, d.currency || 'EUR');
		},
		chip(item) {
			if (!item.match) return { testo: 'Nessuno', colore: 'grey' };
			return METODI_MATCH[item.match.method] || { testo: item.match.method, colore: 'grey' };
		},
		titoloChip(item) {
			if (!item.match) return 'Nessun prodotto del catalogo abbinato';
			const parti = [];
			if (item.match.confidence !== undefined && item.match.confidence !== null && item.match.method === 'ai') parti.push(`confidenza ${Math.round(item.match.confidence * 100)}%`);
			if (item.matchLocked) parti.push('corretto a mano: le nuove scansioni non lo cambiano');
			return parti.join(' · ');
		},
		// aggiorna una riga dopo la correzione dell'abbinamento, senza ricaricare la pagina
		aggiornaRiga(prodotto) {
			const i = this.righe.findIndex(r => r._id === prodotto._id);
			if (i >= 0) this.$set(this.righe, i, { ...this.righe[i], ...prodotto });
		},
		onClear() {
			this.cerca = '';
			this.ricarica();
		},
		ricarica() {
			if (this.opzioni.page !== 1) {
				this.opzioni = { ...this.opzioni, page: 1 }; // il watcher ricarica
				return;
			}
			this.carica();
		},
		// ricerca, filtro e pagina nell'URL: refresh e ritorno dall'abbinamento ripartono da li'
		syncUrl() {
			const query = { ..._.omit(this.$route.query, CHIAVI), site: this.siteId };
			if (this.cerca) query.q = this.cerca;
			if (this.filtro !== 'all') query.abbinati = this.filtro;
			if (this.opzioni.page > 1) query.page = String(this.opzioni.page);
			if (this.opzioni.itemsPerPage !== 50) query.perPage = String(this.opzioni.itemsPerPage);
			if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.replace({ query }).catch(() => {});
			rememberListRoute(this.$route.path, query);
		},
		async carica() {
			this.syncUrl();
			this.caricamento = true;
			const res = await this.$store.dispatch('competitor/fetchProducts', {
				siteId: this.siteId,
				q: this.cerca || undefined,
				matched: this.filtro === 'all' ? undefined : this.filtro,
				page: this.opzioni.page,
				limit: this.opzioni.itemsPerPage > 0 ? this.opzioni.itemsPerPage : 100,
			});
			this.caricamento = false;
			if (res.error) {
				this.$emit('error', testoErrore(res.error));
				return;
			}
			this.righe = res.products || [];
			this.totale = res.total || 0;
		},
	},
};
</script>
